import React, { memo, useEffect, useRef, useState } from 'react';
import { Lightbulb, Brain, Handshake, Rocket, Flame, Cog, Gift, type LucideIcon } from 'lucide-react';
import SectionShell from './ui/Section';
import { SectionHeader } from './ui/SectionHeader';

type WhyItem = {
  icon: LucideIcon;
  title: string;
  description: string;
};

function useInView<T extends HTMLElement>(rootMargin = '100px') {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, root: null, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  return { ref, inView };
}

const Card = memo(function Card({ item }: { item: WhyItem }) {
  const { ref, inView } = useInView<HTMLDivElement>('150px');
  const IconComponent = item.icon;

  return (
    <div
      ref={ref}
      data-inview={inView ? 'true' : 'false'}
      className="
        card-group group relative rounded-2xl p-[1.5px]
        bg-gradient-to-br 
        from-[#d65c30]/50 via-[#1a3b5c]/40 to-[#0d273d]/45
        hover:from-[#d65c30]/70 hover:via-[#1a3b5c]/50 hover:to-[#102f49]/60
        transition-all duration-500
        [contain:layout_paint_style] will-change-transform
        h-full
        [content-visibility:auto] [contain-intrinsic-size:380px]
      "
    >
      {/* Glow */}
      <div
        className="
          pulse-glow pointer-events-none absolute inset-0 rounded-2xl
          opacity-0 group-hover:opacity-100 transition-opacity duration-500
          data-[inview=true]:opacity-100
          prefers-reduced-motion:opacity-0
        "
        style={{
          background:
            'radial-gradient(120% 120% at 50% 0%, rgba(214,92,48,0.15), rgba(26,59,92,0.12) 45%, rgba(192,214,223,0.08) 80%, transparent 100%)',
          filter: 'blur(3px)',
        }}
      />

      <div
        data-card
        className="
          card-content relative 
          bg-gradient-to-br from-[#102f49]/95 via-[#0d273d]/90 to-[#1a3b5c]/95
          backdrop-blur-xl rounded-2xl p-8
          border border-[#C0D6DF]/20 
          transition-all duration-300 
          group-hover:border-[#d65c30]/40
          group-hover:shadow-lg group-hover:shadow-[#d65c30]/20
          flex flex-col h-full
        "
      >
        <div className="mb-6">
          <div
            className="
              w-14 h-14 rounded-xl 
              bg-gradient-to-br from-[#0d273d]/60 to-[#1a3b5c]/80
              ring-1 ring-[#d65c30]/30 backdrop-blur-md 
              flex items-center justify-center 
              shadow-[0_0_20px_rgba(214,92,48,0.25)]
              transition-all duration-300
              group-hover:ring-[#d65c30]/60
              group-hover:shadow-[0_0_30px_rgba(214,92,48,0.4)]
            "
          >
            <IconComponent
              className="
                w-8 h-8 text-[#d65c30] 
                drop-shadow-[0_0_10px_rgba(214,92,48,0.5)]
              "
            />
          </div>
        </div>

        <h3 className="font-montserrat font-bold text-xl bg-gradient-to-r from-white via-[#C0D6DF] to-white bg-clip-text text-transparent mb-3">
          {item.title}
        </h3>
        <p className="font-montserrat text-[#C0D6DF]/90 leading-relaxed">
          {item.description}
        </p>

        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 data-[inview=true]:opacity-100 transition-opacity duration-700 pointer-events-none rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-[#d65c30]/15 via-[#1a3b5c]/10 to-transparent blur-2xl 
                          prefers-reduced-motion:blur-0" />
        </div>
      </div>
    </div>
  );
});

export default function WhatWaitingForYouSection() {
  const why: WhyItem[] = [
    {
      icon: Brain,
      title: 'Quality Networking',
      description:
        "Build connections that bring real value. Meet people who motivate you and share similar goals. Grow through conversations that feel natural and meaningful.",
    },
    {
      icon: Handshake,
      title: 'Collaboration & Opportunities',
      description:
        'Find partners, clients, and co-founders through simple, open communication. Turn small ideas into real projects together. Discover opportunities that appear when the right people meet.',
    },
    {
      icon: Cog,
      title: 'Practical Workshops',
      description:
        'Develop skills through real practice, not theory. Learn step-by-step with hands-on tasks you can apply immediately. Leave each workshop with tools you can use in your work and daily life.',
    },
  ];

  return (
    <SectionShell bgClass="bg-navy-bottom">
      <style>{`
        @keyframes slowPulseGlow {
          0%, 100% {
            opacity: .18;
            transform: scale(1);
            box-shadow:
              0 0 24px rgba(214,92,48,0.3),
              0 0 48px rgba(26,59,92,0.2),
              0 0 72px rgba(192,214,223,0.15);
          }
          50% {
            opacity: .38;
            transform: scale(1.06);
            box-shadow:
              0 0 36px rgba(214,92,48,0.45),
              0 0 72px rgba(26,59,92,0.3),
              0 0 96px rgba(192,214,223,0.25);
          }
        }

        .card-group:hover .pulse-glow { 
          animation: slowPulseGlow 4.5s ease-in-out infinite; 
        }
        .card-group[data-inview="true"] .pulse-glow { 
          animation: slowPulseGlow 6s ease-in-out infinite; 
        }

        @media (prefers-reduced-motion: reduce) {
          .pulse-glow { animation: none !important; }
        }

        .pulse-glow { 
          will-change: transform, opacity, box-shadow; 
        }

        .card-content {
          backface-visibility: hidden;
          transform: translateZ(0);
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
        }
      `}</style>

      <SectionHeader
        eyebrowText="Why Join Us"
        eyebrowIcon={Gift}
        title="What's Waiting for You"
        subtitle="Where bold ideas meet real action and every connection sparks new growth."
        titleGradientClassName="bg-gradient-to-r from-[#0d273d] via-[#d65c30] to-[#1a3b5c]"
        textColorClass="text-white"
        align="center"
      />

      <div
        className="
          grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8
          items-stretch
        "
      >
        {why.map((item, index) => (
          <Card key={index} item={item} />
        ))}
      </div>
    </SectionShell>
  );
}